import {
  Stack,
  Text,
  Group,
  Paper,
  Badge,
  Alert,
  Loader
} from '@mantine/core';
import { ResponsiveDrawer } from '../UI/ResponsiveDrawer';
import {
  IconReplace,
  IconAlertCircle
} from '@tabler/icons-react';
import { DrawerHeader } from '../UI/DrawerHeader';
import { Ingredient, IngredientSubstitution } from '../../types/mealPlan';
import {
  formatIngredientName,
  formatIngredientPreparation,
  formatIngredientQuantity,
} from '../../utils/formatIngredient';

interface SubstitutionDrawerProps {
  opened: boolean;
  onClose: () => void;
  target: Ingredient | null;
  loading: boolean;
  error: string | null;
}

export const SubstitutionDrawer = ({
  opened,
  onClose,
  target,
  loading,
  error
}: SubstitutionDrawerProps) => {
  return (
    <ResponsiveDrawer
      opened={opened}
      onClose={onClose}
      title={
        <DrawerHeader
          icon={<IconReplace size={18} />}
          title={target ? `Substitutes for ${target.name}` : 'Ingredient Substitutions'}
        />
      }
      size="md"
    >
      <Stack gap="md">
        {loading ? (
          <Group gap="sm" justify="center" mt="xl">
            <Loader size="sm" />
            <Text c="dimmed">Finding substitutions...</Text>
          </Group>
        ) : error ? (
          <Alert icon={<IconAlertCircle size={16} />} color="red" radius="lg">
            {error}
          </Alert>
        ) : target?.substitutions && target.substitutions.length > 0 ? (
          <>
            <Text size="sm" c="dimmed">
              Here are viable substitutes for <strong>{target!.name}</strong> in this recipe:
            </Text>
            <Stack gap="sm">
              {target!.substitutions!.map((substitution: IngredientSubstitution, index: number) => (
                <Paper key={index} p="sm" radius="lg" withBorder>
                  <Stack gap="xs">
                    <Group justify="space-between" align="flex-start">
                      <Text fw={500}>
                        {formatIngredientQuantity(substitution)} {formatIngredientName(substitution)}
                        {formatIngredientPreparation(substitution) && (
                          <Text component="span" c="dimmed">
                            {' '}— {formatIngredientPreparation(substitution)}
                          </Text>
                        )}
                      </Text>
                      {substitution.optional && (
                        <Badge size="xs" variant="light" color="gray">
                          optional
                        </Badge>
                      )}
                    </Group>
                    <Text size="sm" c="dimmed">
                      {substitution.rationale}
                    </Text>
                    {substitution.conversionNote && (
                      <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>
                        Note: {substitution.conversionNote}
                      </Text>
                    )}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </>
        ) : target ? (
          <Alert icon={<IconAlertCircle size={16} />} color="yellow" radius="lg">
            No good substitutes found for {target!.name} in this recipe.
          </Alert>
        ) : null}
      </Stack>
    </ResponsiveDrawer>
  );
};